import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

// Global 2-model compare modal.
//
// Two searchable slots (A and B). The full list of comparable models is
// server-rendered into the dialog up front (see shared/arena/_compare_modal)
// and shown by DEFAULT — the search field filters those rows client-side by
// their data-search-text rather than hitting the network. This mirrors the
// per-model "Compare with" picker (arena/models/_compare_with), which also
// shows the whole list and filters in the browser.
//
// The single search field feeds whichever slot is "active"; picking a row fills
// that slot and advances to the empty one. Once both slots hold a model, the
// Compare button navigates (Turbo) to /arena/compare/:a/:b with the two slugs sorted
// alphabetically (the canonical ordering ComparisonsController enforces).
//
// Opening: listens on window for "arena:open-compare-models". An optional
// detail { preselect: "<slug>" } prefills slot A.
//
// Keyboard: Esc closes (native <dialog>), Enter picks the active/first visible
// result (or runs the compare when both slots are filled), arrows move the
// active row.
export default class extends Controller<HTMLElement> {
  static targets = [
    "dialog",
    "input",
    "results",
    "list",
    "groupLabel",
    "result",
    "noMatch",
    "ready",
    "go",
    "slot",
    "slotNameA",
    "slotNameB",
    "slotThumbA",
    "slotThumbB",
    "slotClearA",
    "slotClearB",
  ]

  declare readonly dialogTarget: HTMLDialogElement
  declare readonly inputTarget: HTMLInputElement
  declare readonly listTarget: HTMLElement
  declare readonly groupLabelTarget: HTMLElement
  declare readonly resultTargets: HTMLButtonElement[]
  declare readonly noMatchTarget: HTMLElement
  declare readonly readyTarget: HTMLElement
  declare readonly goTarget: HTMLButtonElement
  declare readonly slotTargets: HTMLElement[]
  declare readonly slotNameATarget: HTMLElement
  declare readonly slotNameBTarget: HTMLElement
  declare readonly slotThumbATarget: HTMLElement
  declare readonly slotThumbBTarget: HTMLElement
  declare readonly slotClearATarget: HTMLElement
  declare readonly slotClearBTarget: HTMLElement

  private picked: { a: PickedModel | null; b: PickedModel | null } = { a: null, b: null }
  private activeSlot: SlotKey = "a"
  private activeIndex = -1

  private onOpen = (event: Event) => this.openFromEvent(event as CustomEvent)

  connect() {
    window.addEventListener("arena:open-compare-models", this.onOpen)
  }

  disconnect() {
    window.removeEventListener("arena:open-compare-models", this.onOpen)
  }

  // ── Open / close ──────────────────────────────────────────────────────
  private openFromEvent(event: CustomEvent) {
    if (this.dialogTarget.open) return
    this.dialogTarget.showModal()
    this.reset()

    const preselect = event.detail?.preselect
    const hasPreselect = typeof preselect === "string" && preselect.length > 0
    if (hasPreselect) this.preselectSlotA(preselect)

    try { window.umami?.track("arena-compare-open", { preselect: hasPreselect ? preselect : null }) } catch {}

    requestAnimationFrame(() => this.inputTarget.focus())
  }

  close() {
    if (this.dialogTarget.open) this.dialogTarget.close()
  }

  onClose() {
    // Nothing async to tear down; reset() runs on the next open.
  }

  // Close only on a true backdrop click (target === the dialog element).
  backdropClose(event: MouseEvent) {
    if (event.target === this.dialogTarget) this.close()
  }

  private reset() {
    this.picked = { a: null, b: null }
    this.activeSlot = "a"
    this.activeIndex = -1
    this.inputTarget.value = ""
    this.renderSlot("a")
    this.renderSlot("b")
    this.highlightActiveSlot()
    this.refreshGo()
    this.updatePlaceholder()
    this.updateGroupLabel()
    this.applyState()
  }

  // ── Slot selection ────────────────────────────────────────────────────
  focusSlot(event: Event) {
    const slot = (event.currentTarget as HTMLElement).dataset.slot as SlotKey
    if (slot !== "a" && slot !== "b") return
    this.activeSlot = slot
    this.highlightActiveSlot()
    this.updatePlaceholder()
    this.updateGroupLabel()
    this.applyState()
    this.inputTarget.focus()
  }

  clearSlot(event: Event) {
    event.stopPropagation()
    const slot = (event.currentTarget as HTMLElement).dataset.slot as SlotKey
    if (slot !== "a" && slot !== "b") return
    this.picked[slot] = null
    this.activeSlot = slot
    this.renderSlot(slot)
    this.highlightActiveSlot()
    this.refreshGo()
    this.updatePlaceholder()
    this.updateGroupLabel()
    this.applyState()
  }

  private highlightActiveSlot() {
    this.slotTargets.forEach((el) => {
      const slot = el.dataset.slot as SlotKey
      el.classList.toggle("is-active", slot === this.activeSlot)
      el.classList.toggle("is-filled", this.picked[slot] !== null)
    })
  }

  private renderSlot(slot: SlotKey) {
    const model = this.picked[slot]
    const nameEl = slot === "a" ? this.slotNameATarget : this.slotNameBTarget
    const thumbEl = slot === "a" ? this.slotThumbATarget : this.slotThumbBTarget
    const clearEl = slot === "a" ? this.slotClearATarget : this.slotClearBTarget

    if (model) {
      nameEl.textContent = model.name
      thumbEl.innerHTML = model.logo
        ? `<img src="${escapeAttr(model.logo)}" alt="" width="32" height="32" loading="lazy">`
        : DASHED_ICON
      clearEl.classList.remove("hidden")
    } else {
      nameEl.textContent = "Pick a model"
      thumbEl.innerHTML = DASHED_ICON
      clearEl.classList.add("hidden")
    }
  }

  // ── Picking ───────────────────────────────────────────────────────────
  pickFromRow(event: Event) {
    const row = event.currentTarget as HTMLElement
    this.pick(modelFromRow(row))
  }

  private pick(model: PickedModel) {
    // The active slot can't hold what the other slot already holds (those rows
    // are hidden), but guard anyway.
    const other: SlotKey = this.activeSlot === "a" ? "b" : "a"
    if (this.picked[other]?.slug === model.slug) return

    this.picked[this.activeSlot] = model
    this.renderSlot(this.activeSlot)

    // Advance to the still-empty slot so the next pick fills it.
    if (this.picked.a && !this.picked.b) this.activeSlot = "b"
    else if (this.picked.b && !this.picked.a) this.activeSlot = "a"

    this.highlightActiveSlot()
    this.inputTarget.value = ""
    this.refreshGo()
    this.updatePlaceholder()
    this.updateGroupLabel()
    this.applyState()
    this.inputTarget.focus()
  }

  // ── Search / filter (client-side over the pre-rendered rows) ────────────
  search() {
    this.applyState()
  }

  // Decide what the results area shows: the "ready" state once both slots are
  // filled (and nothing typed), otherwise the filtered model list.
  private applyState() {
    const query = this.inputTarget.value.trim().toLowerCase()

    if (this.picked.a && this.picked.b && query === "") {
      this.listTarget.classList.add("hidden")
      this.noMatchTarget.classList.add("hidden")
      this.readyTarget.classList.remove("hidden")
      this.activeIndex = -1
      return
    }

    this.readyTarget.classList.add("hidden")
    this.filterRows(query)
  }

  private filterRows(query: string) {
    // Hide BOTH slots' picks, not just the other slot's — a model already
    // chosen in either A or B shouldn't reappear as pickable.
    const pickedSlugs = [this.picked.a?.slug, this.picked.b?.slug]
    let visible = 0

    for (const row of this.resultTargets) {
      const matchesQuery = query === "" || (row.dataset.searchText || "").includes(query)
      const isPicked = pickedSlugs.includes(row.dataset.slug)
      const show = matchesQuery && !isPicked
      row.classList.toggle("hidden", !show)
      row.classList.remove("is-active")
      if (show) visible += 1
    }

    this.activeIndex = -1
    this.listTarget.classList.toggle("hidden", visible === 0)
    this.noMatchTarget.classList.toggle("hidden", visible !== 0)
  }

  // Prefill slot A from an event detail slug (matches a pre-rendered row).
  private preselectSlotA(slug: string) {
    const row = this.resultTargets.find((el) => el.dataset.slug === slug)
    if (!row) return
    this.activeSlot = "a"
    this.pick(modelFromRow(row))
  }

  // ── Keyboard ──────────────────────────────────────────────────────────
  onKeydown(event: KeyboardEvent) {
    const rows = this.visibleRows
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.move(1)
        break
      case "ArrowUp":
        event.preventDefault()
        this.move(-1)
        break
      case "Enter": {
        event.preventDefault()
        const target = this.activeIndex >= 0 ? rows[this.activeIndex] : rows[0]
        if (target) this.pick(modelFromRow(target))
        // No list showing and both slots filled — Enter runs the compare.
        else if (this.picked.a && this.picked.b) this.compare()
        break
      }
      case "Escape":
        // Native <dialog> handles closing.
        break
    }
  }

  private move(delta: number) {
    const rows = this.visibleRows
    if (rows.length === 0) return
    this.activeIndex = (this.activeIndex + delta + rows.length) % rows.length
    rows.forEach((el) => el.classList.remove("is-active"))
    const active = rows[this.activeIndex]
    active.classList.add("is-active")
    active.scrollIntoView({ block: "nearest" })
  }

  private get visibleRows(): HTMLButtonElement[] {
    return this.resultTargets.filter((el) => !el.classList.contains("hidden"))
  }

  // ── Compare action ────────────────────────────────────────────────────
  private refreshGo() {
    this.goTarget.disabled = !(this.picked.a && this.picked.b)
  }

  private updatePlaceholder() {
    this.inputTarget.placeholder = `Search a model for slot ${this.activeSlot.toUpperCase()}`
  }

  private updateGroupLabel() {
    this.groupLabelTarget.textContent = `Models · slot ${this.activeSlot.toUpperCase()}`
  }

  compare() {
    const a = this.picked.a
    const b = this.picked.b
    if (!a || !b) return
    const [slugA, slugB] = [a.slug, b.slug].sort()
    try { window.umami?.track("arena-compare-run", { model_a: slugA, model_b: slugB }) } catch {}
    const path = `/arena/compare/${encodeURIComponent(slugA)}/${encodeURIComponent(slugB)}`
    this.close()
    Turbo.visit(path)
  }
}

type SlotKey = "a" | "b"

interface PickedModel {
  slug: string
  name: string
  sub: string
  logo: string
}

// Read a PickedModel from a pre-rendered result row's data attributes.
function modelFromRow(row: HTMLElement): PickedModel {
  return {
    slug: row.dataset.slug || "",
    name: row.dataset.name || row.dataset.slug || "",
    sub: row.dataset.sub || "",
    logo: row.dataset.logo || "",
  }
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

// Inline lucide glyph for an empty slot thumbnail (server renders the same icon
// elsewhere; kept tiny here for the JS-driven slot swap).
const DASHED_ICON = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="size-4" style="opacity:.5"><path d="M10.1 2.18a9.93 9.93 0 0 1 3.8 0"/><path d="M17.6 3.71a9.95 9.95 0 0 1 2.69 2.7"/><path d="M21.82 10.1a9.93 9.93 0 0 1 0 3.8"/><path d="M20.29 17.6a9.95 9.95 0 0 1-2.7 2.69"/><path d="M13.9 21.82a9.94 9.94 0 0 1-3.8 0"/><path d="M6.4 20.29a9.95 9.95 0 0 1-2.69-2.7"/><path d="M2.18 13.9a9.93 9.93 0 0 1 0-3.8"/><path d="M3.71 6.4a9.95 9.95 0 0 1 2.7-2.69"/></svg>`
